/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	IEntitySearchResultDto
} from '@api/interfaces/entities/entity-search-result.dto.interface';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicComponentLookup
} from '@dynamicComponents/dynamic-component.lookup';
import {
	ContentAnimation
} from '@shared/app-animations';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	CommonListComponent
} from '@shared/dynamic-components/common-list/common-list.component';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IEntityNavigationDefinition
} from '@shared/interfaces/application-objects/entity-navigation-definition.interface';
import {
	IEntityNavigationRequest
} from '@shared/interfaces/application-objects/entity-navigation-request.interface';
import {
	IKeyValuePair
} from '@shared/interfaces/application-objects/key-value-pair.interface';
import {
	ICommonListContext
} from '@shared/interfaces/dynamic-interfaces/common-list-context.interface';
import {
	ICommonListFilter
} from '@shared/interfaces/dynamic-interfaces/common-list-filter.interface';
import {
	ICommonListItem
} from '@shared/interfaces/dynamic-interfaces/common-list-item.interface';
import {
	ICommonListSort
} from '@shared/interfaces/dynamic-interfaces/common-list-sort.interface';
import {
	ISecureMenuItem
} from '@shared/interfaces/secure-menu-item.interface';
import {
	NavigationService
} from '@shared/services/navigation.service';
import {
	ResolverService
} from '@shared/services/resolver.service';
import {
	from,
	Observable,
	Subject,
	Subscription
} from 'rxjs';
import {
	takeUntil
} from 'rxjs/operators';

@Component({
	selector: 'app-global-search',
	templateUrl: './global-search.component.html',
	styleUrls: [
		'./global-search.component.scss',
	],
	animations: [
		ContentAnimation
	]
})

/**
 * A component representing the global entity search functionality
 * with infinite scrolling and comprehensive search capabilities.
 *
 * @export
 * @class GlobalSearchComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @implements {IDynamicComponent<Component, any>}
 */
export class GlobalSearchComponent
implements OnInit, OnDestroy, IDynamicComponent<Component, any>
{
	/**
	 * Initializes a new instance of the GlobalSearchComponent class.
	 *
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance API service used for search operations.
	 * @param {ResolverService} resolverService
	 * The resolver service for dependency resolution in context.
	 * @param {NavigationService} navigationService
	 * The navigation service for centralized entity navigation.
	 * @memberof GlobalSearchComponent
	 */
	public constructor(
		public entityInstanceApiService: EntityInstanceApiService,
		public resolverService: ResolverService,
		public navigationService: NavigationService)
	{
	}

	/**
	 * Reference to the common-list child component.
	 *
	 * @type {CommonListComponent}
	 * @memberof GlobalSearchComponent
	 */
	@ViewChild(CommonListComponent)
	public commonList: CommonListComponent;

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof GlobalSearchComponent
	 */
	public context: IDynamicComponentContext<Component, any>;

	/**
	 * Gets or sets the enabled sorter.
	 *
	 * @type {ICommonListSort}
	 * @memberof GlobalSearchComponent
	 */
	public sorter: ICommonListSort =
		<ICommonListSort>
		{
			name: 'Create Date',
			value: 'id',
			direction: AppConstants.sortDirections.ascending,
			iconAsc: 'fa fa-sort-amount-asc',
			iconDesc: 'fa fa-sort-amount-desc'
		};

	/**
	 * Gets or sets the common list context for displaying search results.
	 *
	 * @type {IDynamicComponentContext<
	 * Component, ICommonListContext<IEntitySearchResultDto>>}
	 * @memberof GlobalSearchComponent
	 */
	public commonListContext:
		IDynamicComponentContext<
			Component,
			ICommonListContext<IEntitySearchResultDto>>;

	/**
	 * Gets or sets the flat array of all search results.
	 *
	 * @type {IEntitySearchResultDto[]}
	 * @memberof GlobalSearchComponent
	 */
	public searchResults: IEntitySearchResultDto[] = [];

	/**
	 * Gets or sets the enabled filters for the common list.
	 * Persists search terms across sort and data refresh operations.
	 *
	 * @type {ICommonListFilter[]}
	 * @memberof GlobalSearchComponent
	 */
	public enabledFilters: ICommonListFilter[] = [];

	/**
	 * Gets the search help tooltip content.
	 * Displayed via CommonList's action tooltip mechanism.
	 *
	 * @type {string}
	 * @memberof GlobalSearchComponent
	 */
	public searchHelpContent: string = AppConstants.empty;

	/**
	 * Subject for managing subscription cleanup on component destruction.
	 *
	 * @private
	 * @type {Subject<void>}
	 * @memberof GlobalSearchComponent
	 */
	private readonly destroy: Subject<void> = new Subject<void>();

	/**
	 * Active search subscription for cancellation management.
	 * Allows explicit cancellation of in-flight searches.
	 *
	 * @private
	 * @type {Subscription | null}
	 * @memberof GlobalSearchComponent
	 */
	private activeSearchSubscription: Subscription | null = null;

	/**
	 * Tracks the current search request sequence number.
	 * Incremented with each new search to ignore stale results.
	 *
	 * @private
	 * @type {number}
	 * @memberof GlobalSearchComponent
	 */
	private searchRequestSequence: number = 0;

	/**
	 * Gets or sets the page size for search results.
	 *
	 * @private
	 * @type {number}
	 * @memberof GlobalSearchComponent
	 */
	private readonly pageSize: number = 50;

	/**
	 * Stores the last search query for re-triggering searches on sort change.
	 *
	 * @private
	 * @type {string | null}
	 * @memberof GlobalSearchComponent
	 */
	private lastSearchQuery: string | null = null;

	/**
	 * On initialization event.
	 * Initializes the global search component.
	 *
	 * @memberof GlobalSearchComponent
	 */
	public ngOnInit(): void
	{
		const tab: string =
			AppConstants.tabWhitespace + AppConstants.tabWhitespace;

		this.searchHelpContent =
			'Use these options to fine-tune your search results.\n\n'
			+ 'Find items containing a specific word or add quotes to find an exact phrase:\n\n'
			+ `${tab}• Example: Smith\n`
			+ `${tab}${tab}• Returns all items containing the word Smith\n`
			+ `${tab}• Example: "John Smith"\n`
			+ `${tab}${tab}• Returns only items including the combination John Smith\n\n`
			+ 'Use a minus sign to exclude search results containing certain words or phrases:\n\n'
			+ `${tab}• Example: -Cancelled\n`
			+ `${tab}${tab}• Excludes items containing the word Cancelled\n`
			+ `${tab}• Example: -"Pending Approval"\n`
			+ `${tab}${tab}• Excludes items containing the phrase Pending Approval\n\n`
			+ 'Combined Example:\n\n'
			+ `${tab}• "New Business" -Cancelled\n`
			+ `${tab}${tab}• Returns results that contain the exact phrase `
			+ 'New Business but exclude anything containing Cancelled.';

		this.commonListContext =
			<IDynamicComponentContext<
				Component,
				ICommonListContext<IEntitySearchResultDto>>>
			{
				source: this,
				data: this.generateCommonListContext()
			};
	}

	/**
	 * On destroy event.
	 * Cleans up subscriptions to prevent memory leaks.
	 *
	 * @memberof GlobalSearchComponent
	 */
	public ngOnDestroy(): void
	{
		this.cancelActiveSearch();
		this.destroy.next();
		this.destroy.complete();
	}

	/**
	 * Performs a search operation using the provided search query.
	 * Resets pagination and clears previous results.
	 * Cancels any in-flight searches before starting a new one.
	 *
	 * @param {string} searchQuery
	 * The search query string.
	 * @returns {void}
	 * @memberof GlobalSearchComponent
	 */
	public performSearch(searchQuery: string): void
	{
		const trimmedQuery: string = searchQuery?.trim() || AppConstants.empty;

		if (trimmedQuery === AppConstants.empty)
		{
			this.cancelActiveSearch();
			this.commonListContext.data.hasValidationError = false;
			this.commonListContext.data.validationMessage = AppConstants.empty;

			if (!AnyHelper.isNull(this.commonList))
			{
				this.commonList.loading = false;
			}

			return;
		}

		if (trimmedQuery.length < AppConstants.search.minimumTermLength)
		{
			this.commonListContext.data.hasValidationError = true;
			this.commonListContext.data.validationMessage =
				AppConstants.search.termTooShortMessage;

			return;
		}

		this.cancelActiveSearch();

		// Increment sequence to ignore old results.
		this.searchRequestSequence++;
		const currentSequence: number = this.searchRequestSequence;

		// Clear previous results and set loading state.
		this.searchResults = [];
		this.commonListContext.data.data = [];
		this.commonListContext.data.hasValidationError = false;
		this.commonListContext.data.validationMessage = AppConstants.empty;

		if (!AnyHelper.isNull(this.commonList))
		{
			this.commonList.loading = true;
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			AppConstants.empty;

		const orderBy: string = this.sorter
			? `${this.sorter.value} ${this.sorter.direction}`
			: undefined;

		this.activeSearchSubscription =
			this.performStandardSearch(
				trimmedQuery,
				orderBy,
				this.pageSize,
				currentSequence);
	}

	/**
	 * Handles the result item click event.
	 * Navigates to the selected entity instance.
	 *
	 * @param {IEntitySearchResultDto} result
	 * The selected search result.
	 * @returns {Promise<void>}
	 * A promise that resolves when navigation is complete.
	 * @memberof GlobalSearchComponent
	 */
	public async onResultClick(
		result: IEntitySearchResultDto): Promise<void>
	{
		await this.navigateToResult(result);
	}

	/**
	 * Navigates to the detail view of the selected entity instance.
	 *
	 * @param {IEntitySearchResultDto} result
	 * The entity instance to navigate to.
	 * @returns {Promise<void>}
	 * A promise that resolves when navigation is complete.
	 * @memberof GlobalSearchComponent
	 */
	public async navigateToResult(
		result: IEntitySearchResultDto): Promise<void>
	{
		const navigationDefinition: IEntityNavigationDefinition =
			await this.navigationService.getEntityNavigationDefinition(
				<IEntityNavigationRequest>
				{
					id: result.id,
					entityType: result.entityType,
					entityTypeGroup: result.entityTypeGroup
				});

		await this.navigationService.navigateToEntityDefinition(
			navigationDefinition);
	}

	/**
	 * Maps a search result entity to a common list item for display.
	 *
	 * @param {IEntitySearchResultDto} searchResult
	 * The search result entity to map.
	 * @returns {ICommonListItem<IEntitySearchResultDto>}
	 * The mapped common list item.
	 * @memberof GlobalSearchComponent
	 */
	public mapSearchResultToListItem(
		searchResult: IEntitySearchResultDto):
		ICommonListItem<IEntitySearchResultDto>
	{
		// Use title as the description line with text-link styling.
		const descriptionLine: string =
			'<span class="text-link description-line-link theme-color">'
				+ searchResult.title
				+ '</span>';

		// Format key/value pairs as a single line: "key: value, key2: value2"
		const informationLine: string =
			searchResult.summary && searchResult.summary.length > 0
				? searchResult.summary
					.map((pair: IKeyValuePair) => `${pair.key}: ${pair.value}`)
					.join(', ')
				: AppConstants.empty;

		const actions: ISecureMenuItem[] = [];

		const listItem: ICommonListItem<IEntitySearchResultDto> =
			<ICommonListItem<IEntitySearchResultDto>>
			{
				item: searchResult,
				descriptionLineFormat: descriptionLine,
				informationLineFormat: informationLine,
				informationIcons: [],
				actions: actions
			};

		// Set the key-value pairs for the list item.
		(listItem.item as any).keyValuePairs =
			searchResult.summary || [];

		return listItem;
	}

	/**
	 * Handles sort change events from the common list.
	 * Re-triggers the search with the new sort order using existing
	 * search term.
	 *
	 * @param {any} _source
	 * The source component triggering the event.
	 * @param {ICommonListSort} sorter
	 * The selected sorter configuration.
	 * @memberof GlobalSearchComponent
	 */
	public handleSortChange(
		_source: any,
		sorter: ICommonListSort): void
	{
		this.sorter = sorter;
		this.commonListContext.data.sorters = [sorter];

		// Re-trigger search with new sort order if we have a search query.
		const lastSearchQuery: string = this.getLastSearchQuery();
		if (lastSearchQuery)
		{
			this.performSearch(lastSearchQuery);
		}
	}

	/**
	 * Handles filter change events from the common list.
	 * Only triggers backend search when search term actually changes.
	 * Preserves search term across sort operations.
	 *
	 * @param {any} _source
	 * The source component triggering the event.
	 * @param {ICommonListFilter[]} filters
	 * The active filters (includes search terms).
	 * @memberof GlobalSearchComponent
	 */
	public handleFilterChange(
		_source: any,
		filters: ICommonListFilter[]): void
	{
		// Search terms are added as filters with the search text as the name.
		// Get the most recent filter (last in array) as the search query.
		if (filters && filters.length > 0)
		{
			const searchFilter: ICommonListFilter =
				filters.at(-1);
			const searchQuery: string = searchFilter.name;

			// Only trigger search if different from last search term.
			if (this.lastSearchQuery !== searchQuery)
			{
				this.performSearch(searchQuery);
				this.lastSearchQuery = searchQuery;
			}

			// Only store the current filter since showChips is false.
			this.enabledFilters = [searchFilter];
		}
		else
		{
			// No filters - clear search results only if we had a query before.
			if (this.lastSearchQuery !== null)
			{
				this.lastSearchQuery = null;
				this.cancelActiveSearch();
				this.searchResults = [];
				this.refreshCommonListContext();
			}

			this.enabledFilters = [];
		}
	}

	/**
	 * Gets the last search query that was executed.
	 *
	 * @private
	 * @returns {string | null}
	 * The last search query or null if no search has been performed.
	 * @memberof GlobalSearchComponent
	 */
	private getLastSearchQuery(): string | null
	{
		return this.lastSearchQuery;
	}

	/**
	 * Generates the common list context structure.
	 * Called once during initialization to create the context
	 * that will be mutated for data updates.
	 *
	 * @private
	 * @returns {ICommonListContext<IEntitySearchResultDto>}
	 * The common list context configuration.
	 * @memberof GlobalSearchComponent
	 */
	private generateCommonListContext():
		ICommonListContext<IEntitySearchResultDto>
	{
		return <ICommonListContext<IEntitySearchResultDto>>
		{
			data: [],
			supportMarkdown: true,
			searchable: true,
			sortable: true,
			showChips: false,
			hasValidationError: false,
			validationMessage: AppConstants.empty,
			sorters: [
				this.sorter
			],
			actions: [
				<ISecureMenuItem>
				{
					icon: 'fa fa-question-circle',
					label: this.searchHelpContent,
					command: () => {},
					securityRightCategory: null,
					securityRightType: null,
					securityRightPath: null
				}
			],
			showGroupCounts: true,
			hideLineTwoOnExpand: true,
			listItemComponent:
			DynamicComponentLookup.supportedTypes
				.keyValuePairComponent,
			getGroupKey:
				(item: ICommonListItem<IEntitySearchResultDto>) =>
					item.item.entityTypeGroup,
			getGroupTitle:
				(item: ICommonListItem<IEntitySearchResultDto>) =>
					StringHelper.beforeCapitalSpaces(
						StringHelper.getLastSplitValue(
							item.item.entityType,
							AppConstants.characters.period)),
			onSortChange: (source, sorter) =>
				this.handleSortChange(source, sorter),
			onFilterChange: (source, filters) =>
				this.handleFilterChange(source, filters),
			onDescriptionClick:
				async (_event: MouseEvent,
					listItem: ICommonListItem<IEntitySearchResultDto>) =>
				{
					await this.navigateToResult(listItem.item);
				}
		};
	}

	/**
	 * Refreshes the common list context by updating data only.
	 * Mutates existing context instead of recreating to preserve
	 * component lifecycle. Follows Files component pattern.
	 *
	 * @private
	 * @memberof GlobalSearchComponent
	 */
	private refreshCommonListContext(): void
	{
		const listItems: ICommonListItem<IEntitySearchResultDto>[] =
			this.searchResults.map(
				(searchResult: IEntitySearchResultDto) =>
					this.mapSearchResultToListItem(searchResult));

		this.commonListContext.data.data = listItems;

		if (!AnyHelper.isNull(this.commonList))
		{
			this.commonList.refresh();

			if (this.commonListContext.data.data.length > 0)
			{
				this.commonList.loading = false;
			}
		}
	}

	/**
	 * Cancels the currently active search subscription if one exists.
	 * Prevents race conditions by stopping in-flight requests.
	 *
	 * @private
	 * @memberof GlobalSearchComponent
	 */
	private cancelActiveSearch(): void
	{
		if (this.activeSearchSubscription)
		{
			this.activeSearchSubscription.unsubscribe();
			this.activeSearchSubscription = null;
		}
	}

	/**
	 * Performs a standard search using REST API.
	 * Returns all results as a single batch from the server.
	 *
	 * @private
	 * @param {string} searchQuery
	 * The search query string to send to the server.
	 * @param {string} orderBy
	 * The order by clause for sorting results.
	 * @param {number} limit
	 * The maximum number of results to return.
	 * @param {number} currentSequence
	 * The sequence number for ignoring stale results.
	 * @returns {Subscription}
	 * The subscription for the search operation.
	 * @memberof GlobalSearchComponent
	 */
	private performStandardSearch(
		searchQuery: string,
		orderBy: string,
		limit: number,
		currentSequence: number): Subscription
	{
		const searchObservable: Observable<IEntitySearchResultDto[]> =
			from(
				this.entityInstanceApiService.search(
					searchQuery,
					undefined, // entityTypeGroups - search all types
					orderBy,
					0, // offset - always start from beginning
					limit));

		return searchObservable
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (results: IEntitySearchResultDto[]) =>
				{
					if (currentSequence === this.searchRequestSequence)
					{
						this.searchResults = results;
						this.refreshCommonListContext();

						// Handle zero results case.
						if (results.length === 0 && this.commonList)
						{
							this.commonList.loading = false;
							this.commonListContext.data.emptyMessage =
								AppConstants.noResultsFoundMessage;
						}
					}
				},
				error: (error: any) =>
				{
					if (currentSequence === this.searchRequestSequence)
					{
						this.searchResults = [];
						this.commonListContext.data.data = [];

						if (!AnyHelper.isNull(this.commonList))
						{
							this.commonList.loading = false;
						}

						throw error;
					}
				}
			});
	}
}